import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { settingDefinitionApi } from "../../api/settingDefinationApi";
import type { SettingDefinition } from "../../types/settingDefination.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";

interface SettingDefinitionsState {
  items: SettingDefinition[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: SettingDefinitionsState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const settingDefinitionsSlice = createSlice({
  name: "settingDefinitions",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setSettingDefinitions: (
      state,
      action: PayloadAction<SettingDefinition[]>,
    ) => {
      state.items = action.payload;
    },
    appendSettingDefinitions: (
      state,
      action: PayloadAction<SettingDefinition[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleSettingDefinitionLocally: (
      state,
      action: PayloadAction<SettingDefinition>,
    ) => {
      state.items = [action.payload, ...state.items];
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetSettingDefinitionList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleSettingDefinition: (
      state,
      action: PayloadAction<
        Partial<SettingDefinition> & { key?: string; id?: string }
      >,
    ) => {
      const index = state.items.findIndex((item) =>
        action.payload.id
          ? item.id === action.payload.id
          : item.key === action.payload.key,
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleSettingDefinition: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => item.id !== action.payload && item.key !== action.payload,
      );
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setSettingDefinitions,
  appendSettingDefinitions,
  addSingleSettingDefinitionLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetSettingDefinitionList,
  updateSingleSettingDefinition,
  removeSingleSettingDefinition,
} = settingDefinitionsSlice.actions;

// Async Thunks

export const fetchSettingDefinitions = (payload: any = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().settingDefinitions;

    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex
      : state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        sortDecending: true,
        providerId: payload.providerId || "",
        category: payload.category || "",
        searchText: payload.searchText || "",
        status: payload.status || null,
        ids: payload.ids || [],
      };
      const response =
        await settingDefinitionApi.searchSettingDefinitions(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (
        currentLoadedCount >= totalCount ||
        fetchedData.length < (payload.pageSize || 10)
      ) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setSettingDefinitions(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendSettingDefinitions(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Setting Definitions error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.fetchError", {
          defaultValue: "Failed to fetch setting definitions",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addSettingDefinition = (data: Partial<SettingDefinition>) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));
      const res = await settingDefinitionApi.createSettingDefinition(data);
      const createdItem = res?.data || res;

      if (createdItem && (createdItem.id || createdItem.key)) {
        dispatch(addSingleSettingDefinitionLocally(createdItem));
      } else {
        dispatch(fetchSettingDefinitions({ pageIndex: 0 }));
      }

      toast.success(
        i18n.t("common:slices.settingDefinitions.addSuccess", {
          defaultValue: "Setting definition created successfully",
        }),
      );
      return createdItem;
    } catch (error) {
      console.error("Create Setting Definition error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.addError", {
          defaultValue: "Failed to create setting definition",
        }),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editSettingDefinition = (
  data: Partial<SettingDefinition> & ({ id: string } | { key: string }),
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));
      const res = await settingDefinitionApi.updateSettingDefinition(data);
      const updatedItem = res?.data || data;

      dispatch(updateSingleSettingDefinition(updatedItem));

      toast.success(
        i18n.t("common:slices.settingDefinitions.editSuccess", {
          defaultValue: "Setting definition updated successfully",
        }),
      );
      return updatedItem;
    } catch (error) {
      console.error("Edit Setting Definition error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.editError", {
          defaultValue: "Failed to update setting definition",
        }),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const activateSettingDefinitionAsync = (idOrKey: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(
        updateSingleSettingDefinition({
          ...(idOrKey.includes("-") ? { id: idOrKey } : { key: idOrKey }),
          status: "Accept",
        }),
      );

      await settingDefinitionApi.activateSettingDefinition(idOrKey);

      toast.success(
        i18n.t("common:slices.settingDefinitions.activateSuccess", {
          defaultValue: "Setting definition activated",
        }),
      );
    } catch (error) {
      dispatch(
        updateSingleSettingDefinition({
          ...(idOrKey.includes("-") ? { id: idOrKey } : { key: idOrKey }),
          status: "Reject",
        }),
      );
      console.error("Activate Setting Definition error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.activateError", {
          defaultValue: "Failed to activate setting definition",
        }),
      );
    }
  };
};

export const deactivateSettingDefinitionAsync = (idOrKey: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(
        updateSingleSettingDefinition({
          ...(idOrKey.includes("-") ? { id: idOrKey } : { key: idOrKey }),
          status: "Reject",
        }),
      );

      await settingDefinitionApi.deActivateSettingDefinition(idOrKey);

      toast.success(
        i18n.t("common:slices.settingDefinitions.deactivateSuccess", {
          defaultValue: "Setting definition deactivated",
        }),
      );
    } catch (error) {
      dispatch(
        updateSingleSettingDefinition({
          ...(idOrKey.includes("-") ? { id: idOrKey } : { key: idOrKey }),
          status: "Accept",
        }),
      );
      console.error("Deactivate Setting Definition error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.deactivateError", {
          defaultValue: "Failed to deactivate setting definition",
        }),
      );
    }
  };
};

export const removeSettingDefinition = (idOrKey: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleSettingDefinition(idOrKey));

      await settingDefinitionApi.removeSettingDefinition(idOrKey);

      toast.success(
        i18n.t("common:slices.settingDefinitions.removeSuccess", {
          defaultValue: "Setting definition removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Setting Definition error:", error);
      toast.error(
        i18n.t("common:slices.settingDefinitions.removeError", {
          defaultValue: "Failed to remove setting definition",
        }),
      );
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default settingDefinitionsSlice.reducer;
